import React, {
    MouseEventHandler,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useReducer,
    ForwardedRef,
    HTMLAttributes,
    FocusEvent,
    memo,
} from 'react';
import ReactDOM from 'react-dom';
import BEMHelper from 'react-bem-helper';
import { isMobile } from '../../utils/client-detection';
// eslint-disable-next-line import/named
import { Config, PopperOptions, usePopperTooltip, TriggerType } from 'react-popper-tooltip';
import 'react-popper-tooltip/dist/styles.css';

import { concatClasses } from '../../utils/dom';
import { ObservableContainer } from '../../utils/observable-container';

import '../../common/fonts.less';
import './style.less';

export const observableBuiltin = new ObservableContainer(false);

export const classesTooltip = new BEMHelper('Tooltip');
export const classesTooltipContainer = new BEMHelper('tooltip-container');

type TooltipStyles = 'tooltip-style-ebony' | 'tooltip-style-snow' | 'tooltip-style-ash';

type TooltipConfig = Pick<Config, 'delayHide' | 'interactive' | 'placement' | 'offset'>;

const defaultTooltipConfig: TooltipConfig = {
    delayHide: 0,
    interactive: false,
    placement: 'auto',
    offset: [0, 7],
};

// Value for situations when width is not specified for tooltip.
const TOOLTIP_NO_WIDTH = -1;

export type TooltipProps = {
    text: string | React.ReactNode;
    children: string | React.ReactNode | React.ReactNode[];
    tooltipStyle?: TooltipStyles;
    force?: boolean;
    closeAction?: MouseEventHandler<HTMLSpanElement>;
    className?: string;
    tooltipContainerClassName?: string;
    id?: string;
    portalMode?: boolean;
    portalRef?: RefObject<HTMLElement>;
    onVisibleChange?: Config['onVisibleChange'];
    textAlign?: 'left' | 'center' | 'right';
    arrowAlign?: boolean;
    showTooltipForMobile?: boolean;
    trigger?: TriggerType | TriggerType[] | null;
} & TooltipConfig;

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
        text,
        children,
        className = '',
        closeAction,
        tooltipStyle,
        force,
        tooltipContainerClassName,
        interactive,
        delayHide,
        ...rest
    } = props;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    const [builtin, setBuiltin] = useState(observableBuiltin.value);

    useEffect(() => observableBuiltin.subscribe(value => setBuiltin(value)), []);

    if (builtin && typeof text === 'string') {
        const wrapperClasses = concatClasses('tt-wrapper', !!closeAction && 'closable', className);
        const targetClasses = classesTooltip('tooltip-target');

        return (
            <div {...classesTooltip('', '', wrapperClasses)} title={text} ref={ref} {...rest}>
                <div {...targetClasses}>{children}</div>
            </div>
        );
    }

    return <TooltipComponentMemoized {...props} />;
});

const TooltipComponent = forwardRef(function TooltipComponent(
    props: TooltipProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    const {
        text,
        children,
        className = '',
        tooltipContainerClassName = '',
        id,
        tooltipStyle = '',
        force,
        closeAction,
        delayHide = defaultTooltipConfig.delayHide,
        interactive = defaultTooltipConfig.interactive,
        placement = defaultTooltipConfig.placement,
        offset = defaultTooltipConfig.offset,
        portalMode = true,
        portalRef,
        onVisibleChange,
        textAlign,
        showTooltipForMobile,
        trigger,
        arrowAlign = false,
        ...rest
    } = props;

    const [showOnTabFocus, setShowOnTabFocus] = useState(false);
    const tooltipTextRef = useRef<HTMLSpanElement>(null);
    const prevTextRef = useRef<TooltipProps['text']>('');
    const tooltipWidthRef = useRef<number>();
    const [, rerender] = useReducer(v => v + 1, 0);

    const appliedConfig: Config = useMemo(
        () => ({
            ...defaultTooltipConfig,
            ...{
                delayHide,
                interactive,
                placement,
                offset,
                onVisibleChange,
                trigger,
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [delayHide, interactive, placement, offset, onVisibleChange]
    );

    const popperOptions: PopperOptions = useMemo(
        () => ({
            modifiers: [
                {
                    name: 'preventOverflow',
                    options: {
                        padding: { left: 10, right: 20 },
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset,
                    },
                },
                {
                    name: 'limitWidthByText',
                    enabled: !closeAction,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => {
                        if (tooltipWidthRef.current !== TOOLTIP_NO_WIDTH) {
                            return;
                        }

                        delete state.styles.popper.width;

                        const tooltipTextElem = tooltipTextRef?.current;
                        if (tooltipTextElem) {
                            if (
                                tooltipTextElem?.childNodes.length === 1 &&
                                tooltipTextElem?.firstChild?.nodeType === 3
                            ) {
                                const newTooltipWidth =
                                    tooltipTextElem.getBoundingClientRect()?.width;
                                if (
                                    newTooltipWidth > 0 &&
                                    Math.abs((tooltipWidthRef.current ?? 0) - newTooltipWidth) >= 2
                                ) {
                                    tooltipWidthRef.current = newTooltipWidth;
                                    state.styles.popper.width = `${newTooltipWidth}px`;
                                    state.reset = true;
                                    rerender();
                                }
                            }
                        }
                    },
                },
                {
                    name: 'arrowAlign',
                    enabled: arrowAlign,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => {
                        if (state.placement === 'right' || state.placement === 'left') {
                            state.styles.arrow.top = '50%';
                            state.styles.arrow.transform = 'translate(0, -50%)';
                        } else if (state.placement === 'top' || state.placement === 'bottom') {
                            state.styles.arrow.left = '50%';
                            state.styles.arrow.transform = 'translate(-50%, 0)';
                        }
                    },
                },
            ],
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offset, tooltipWidthRef, tooltipTextRef?.current, closeAction]
    );

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible, triggerRef } =
        usePopperTooltip(appliedConfig, popperOptions);

    const targetClasses = classesTooltip('tooltip-target');

    useEffect(() => {
        if (prevTextRef.current === text) {
            return;
        }

        prevTextRef.current = text;
        tooltipWidthRef.current = TOOLTIP_NO_WIDTH;
        rerender();
    }, [text]);

    const tooltipProps = useMemo(() => {
        const tooltipContainerClasses = `tooltip ${tooltipContainerClassName} ${className}`;
        const tooltipPropsArgs = {
            ...classesTooltipContainer(
                '',
                [tooltipStyle, 'rcv', textAlign ? `textAlign--${textAlign}` : ''],
                tooltipContainerClasses
            ),
            'data-at': 'tooltip-content',
        };

        return { ...getTooltipProps(tooltipPropsArgs) } as HTMLAttributes<HTMLDivElement>;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getTooltipProps, tooltipStyle, tooltipContainerClassName, textAlign]);

    const [isForceHidden, setIsForceHidden] = useState(false);
    useEffect(() => {
        setIsForceHidden(false);
    }, [visible]);

    if (tooltipWidthRef.current === TOOLTIP_NO_WIDTH || prevTextRef.current !== text) {
        delete tooltipProps.style?.width;
    }

    let tooltip = (
        <div ref={setTooltipRef} {...tooltipProps}>
            {closeAction ? (
                <div {...classesTooltipContainer('content')}>
                    <span ref={tooltipTextRef} {...classesTooltipContainer('text')}>
                        {text}
                    </span>
                    <span
                        onClick={event => {
                            closeAction(event);
                            setIsForceHidden(true);
                        }}
                        className='closing-cross'
                    />
                </div>
            ) : (
                <span ref={tooltipTextRef} {...classesTooltipContainer('text')}>
                    {text}
                </span>
            )}
            <div {...getArrowProps({ className: 'tooltip-arrow' })} />
        </div>
    );

    let portalParent;
    if (portalMode) {
        if (portalRef?.current) {
            portalParent = portalRef.current;
        } else {
            const currentDocument = triggerRef?.ownerDocument;
            if (currentDocument) {
                portalParent =
                    currentDocument.getElementById('mount-point') ||
                    currentDocument.getElementById('root');
            }
        }
    }

    if (portalParent) {
        tooltip = ReactDOM.createPortal(tooltip, portalParent);
    }

    const showTooltipOnTabFocus = useCallback((e: FocusEvent<HTMLElement>) => {
        if (e.target.closest('.accessibility-mode')) {
            setShowOnTabFocus(true);
        }
    }, []);

    const hideTooltipOnTabFocus = useCallback(() => {
        setShowOnTabFocus(false);
    }, []);

    const showTooltip = useMemo(
        () =>
            !isForceHidden &&
            force !== false &&
            text &&
            (showOnTabFocus || force || (visible && (!isMobile || showTooltipForMobile))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [force, text, showOnTabFocus, visible, showTooltipForMobile]
    );

    useEffect(() => {
        if (!showTooltip) {
            tooltipWidthRef.current = TOOLTIP_NO_WIDTH;
            rerender();
        }
    }, [showTooltip]);

    const wrapperClasses = concatClasses('tt-wrapper', !!closeAction && 'closable', className);

    return (
        <div id={id} {...classesTooltip('', '', wrapperClasses)} ref={ref} {...rest}>
            <div
                {...targetClasses}
                ref={setTriggerRef}
                onFocus={showTooltipOnTabFocus}
                onBlur={hideTooltipOnTabFocus}>
                {children}
            </div>
            {showTooltip && tooltip}
        </div>
    );
});

const TooltipComponentMemoized = memo(TooltipComponent);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
